// src/components/features/ProductImprovementHub/ProductHubRow.js
import React, { useMemo, useState } from 'react';
// --- THIS IS THE FIX ---
import { ChevronRight, Target, Edit3, X, HelpCircle, CheckSquare, RefreshCw, DatabaseZap } from 'lucide-react'; // Added DatabaseZap
// --- END OF FIX ---
import { PRIMARY_SUBJECT_ORDER } from './ProductHubConstants';

// --- HELPER COMPONENTS (Now defined locally) ---
const ScoreRing = ({ score = 0, onClick = null, className = '' }) => {
    const ringStyle = (s) => {
        const val = Math.max(0, Math.min(100, Number(s) || 0));
        return { backgroundImage: `conic-gradient(#0ea5e9 ${val * 3.6}deg, #e0f2fe 0deg)` };
    };
    return (
        <div
            className={`shrink-0 w-16 h-16 rounded-full grid place-items-center p-1 ${onClick ? 'cursor-pointer' : ''} ${className}`}
            style={ringStyle(score)}
            onClick={onClick}
        >
            <div className="w-full h-full rounded-full bg-white grid place-items-center text-sky-700 font-bold text-xl shadow-inner ring-1 ring-sky-100">
                {Math.round(Math.max(0, Math.min(100, Number(score) || 0)))}
            </div>
        </div>
    );
};

const Pill = ({ children, tone = 'slate', onClick = null, onRemove = null, className = '' }) => {
    const tones = {
        slate: 'bg-slate-100 text-slate-700', purple: 'bg-purple-100 text-purple-800',
        amber: 'bg-amber-100 text-amber-800', green: 'bg-green-100 text-green-800',
        red: 'bg-red-100 text-red-800', blue: 'bg-sky-100 text-sky-800'
    };
    const baseClasses = `inline-flex items-center text-xs font-semibold rounded-full ${tones[tone]} ${className}`;
    const contentClasses = onRemove ? "pl-2.5 pr-1.5 py-1" : "px-2.5 py-1";
    const content = (
        <>
            <span className={contentClasses}>{children}</span>
            {onRemove && (
                <button onClick={onRemove} className="ml-1 p-0.5 rounded-full hover:bg-black/10 flex-shrink-0" aria-label="Remove tag">
                    <X size={12} />
                </button>
            )}
        </>
    );
    if (onClick) return <button onClick={onClick} className={`${baseClasses} hover:brightness-95 transition-all`}>{content}</button>;
    return <span className={baseClasses}>{content}</span>;
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'Never';
  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    if (isNaN(date.getTime())) return 'Error';
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ', ' +
           date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return 'Error';
  }
};
// --- End Helper Components ---

// --- NEW: Row Action Button ---
const ActionButton = ({ icon: Icon, text, onClick, disabled = false, title = '' }) => (
    <button
        onClick={(e) => {
            e.stopPropagation();
            onClick();
        }}
        disabled={disabled}
        title={title}
        className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 bg-white border border-slate-300 rounded-md px-2.5 py-1.5 hover:bg-slate-50 hover:text-slate-900 transition-all disabled:opacity-50 disabled:bg-slate-100 disabled:cursor-not-allowed"
    >
        <Icon size={14} className={disabled ? "animate-spin" : ""} />
        <span>{text}</span>
    </button>
);


const ProductRow = ({
  product, selected, onSelect,
  onMarkProductUpdated, onMarkUploadedToTpt,
  onShowGraphPopup,
  onGoToRewrite,
  onRefreshTags, // <-- NEW
  isRefreshing, // <-- NEW
}) => {

  // --- (All useMemo hooks for tags remain the same) ---
  const subjectAreas = useMemo(() => {
    const manual = product.subjectTags || [];
    const auto = product._subjectAreaTags || [];
    const allAreas = new Set([...auto]);
    manual.forEach(tag => { if (PRIMARY_SUBJECT_ORDER.includes(tag)) allAreas.add(tag); });
    return Array.from(allAreas).sort((a,b) => PRIMARY_SUBJECT_ORDER.indexOf(a) - PRIMARY_SUBJECT_ORDER.indexOf(b));
  }, [product.subjectTags, product._subjectAreaTags]);

  const specificSubjects = useMemo(() => {
    const manual = product.subjectTags || [];
    const auto = product._specificSubjectTags || [];
    const allSpecifics = new Set([...auto]);
     manual.forEach(tag => { if (!PRIMARY_SUBJECT_ORDER.includes(tag)) allSpecifics.add(tag); });
    return Array.from(allSpecifics).sort();
  }, [product.subjectTags, product._specificSubjectTags]);

  const gradesToDisplay = useMemo(() => {
      const manual = product.gradeTags || [];
      const auto = product._gradeRefs || [];
      return Array.from(new Set([...manual, ...auto])).sort((a, b) => {
          const toNum = (g) => { if (g === 'PreK') return -1; if (g === 'K') return 0; if (g === 'Middle School') return 7.5; if (g === 'High School') return 10.5; const num = parseInt(g, 10); return isNaN(num) ? 100 : num; };
          return toNum(a) - toNum(b);
      });
  }, [product.gradeTags, product._gradeRefs]);

  const seasonalTagsToDisplay = useMemo(() => {
      const manual = product.seasonalTags || [];
      const auto = product._seasonalRefs || [];
      return Array.from(new Set([...manual, ...auto]));
  }, [product.seasonalTags, product._seasonalRefs]);

  const rtype = useMemo(() => {
    const manual = product.resourceTypeTags || [];
    const auto = product._typeRefs || [];
    const allTypes = Array.from(new Set([...manual, ...auto]));
    return allTypes[0];
  }, [product.resourceTypeTags, product._typeRefs]);

  const needsRefresh = useMemo(() => product._tags.some(t => t.key === 'needs_refresh'), [product._tags]);

  const [showAllKeywords, setShowAllKeywords] = useState(false);
  const displayedKeywords = useMemo(() => {
      const pinnedFirst = [...(product.allKeywords || [])].sort((a, b) => {
          const pa = product.pinnedKeywords?.includes(a.keyword) ? 1 : 0;
          const pb = product.pinnedKeywords?.includes(b.keyword) ? 1 : 0;
          if (pb !== pa) return pb - pa;
          return (a.curr ?? Infinity) - (b.curr ?? Infinity);
      });
      return showAllKeywords ? pinnedFirst : pinnedFirst.slice(0, 4);
  }, [product.allKeywords, product.pinnedKeywords, showAllKeywords]);
  // --- (End of useMemo hooks) ---

  // --- NEW: Get the price ---
  const price = product.price ? Number(product.price) : 0;

  const handleKeywordClick = (e, kw) => {
      e.stopPropagation();
      onShowGraphPopup?.(kw.keyword);
  };

  const renderKwButton = (kw) => {
    const rank = kw?.curr ?? '—';
    const delta = kw?.delta;
    const up = typeof delta === 'number' && delta > 0;
    const down = typeof delta === 'number' && delta < 0;
    const trend = up ? '↑' : down ? '↓' : '•';
    const tone = up ? 'text-green-600' : down ? 'text-red-600' : 'text-slate-600';
    return (
      <button
        key={kw.keyword}
        className="text-xs font-semibold px-2 py-1 rounded-full border bg-white hover:bg-sky-50 flex items-center gap-1"
        title={`View rank history for "${kw.keyword}"`}
        onClick={(e) => handleKeywordClick(e, kw)}
      >
        {kw.keyword}
        <span className={`ml-1 ${tone}`}>{trend} #{rank}{typeof delta === 'number' ? ` (${Math.abs(delta)})` : ''}</span>
      </button>
    );
  };

  return (
    <div
      data-product-id={product.id}
      onClick={() => onSelect()}
      className={`p-4 bg-white border transition-all cursor-pointer ${selected ? 'border-slate-400 shadow-sm rounded-t-xl' : 'border-slate-200 hover:border-slate-300 rounded-xl'}`}
    >
      <div className="flex items-start gap-4">
        <ScoreRing score={product.audit.score} />
        <div className="flex-1 min-w-0">

          {/* --- MODIFIED: Added Price Pill --- */}
          <div className="flex items-center gap-3 mb-1 min-w-0">
            <h4 className="font-bold tracking-tight text-slate-800 truncate">{product.productTitle}</h4>
            <Pill tone={product._health.tone} className="flex-shrink-0">{product._health.label}</Pill>
            {price > 0 && (
              <Pill tone="green" className="flex-shrink-0 font-bold">
                ${price.toFixed(2)}
              </Pill>
            )}
          </div>
          {/* --- END MODIFIED ROW --- */}

          <p className="text-sm text-slate-600 line-clamp-1">{product.audit.summary}</p>

          <div className="mt-2 flex items-center gap-1.5 flex-wrap">
            {seasonalTagsToDisplay.map(s => <Pill key={s} tone="purple" className="px-2 py-0.5">{s}</Pill>)}
            {subjectAreas.map(s => <Pill key={s} tone="green" className="px-2 py-0.5">{s}</Pill>)}
            {specificSubjects.map(s => <Pill key={s} tone="blue" className="px-2 py-0.5">{s}</Pill>)}
            {gradesToDisplay.map(g => <Pill key={g} tone="slate" className="px-2 py-0.5">{g === 'K' || g === 'PreK' ? g : (g === 'Middle School' || g === 'High School' ? g : `Grade ${g}`)}</Pill>)}
            {rtype && <Pill tone="amber" className="px-2 py-0.5">{rtype}</Pill>}
            {needsRefresh && <Pill tone="slate" className="px-2 py-0.5">Needs Refresh</Pill>}
          </div>

          <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
              <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"> <Target size={14} className="text-slate-400" /> <span className="text-xs font-bold text-slate-600">Tracked Keywords</span> </div>
                  <button className="text-xs font-semibold px-2 py-1 rounded-md border bg-white hover:bg-slate-50" onClick={(e) => { e.stopPropagation(); setShowAllKeywords(s => !s); }}> {showAllKeywords ? 'Less' : 'More'} </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {displayedKeywords.length ? displayedKeywords.map(kw => renderKwButton(kw)) : <span className="text-xs text-slate-400">No keywords tracked.</span>}
              </div>
          </div>

          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap justify-between">
              <div className="flex items-center gap-2">
                <ActionButton
                    icon={Edit3}
                    text="Rewrite"
                    onClick={onGoToRewrite}
                    disabled={isRefreshing}
                    title="Jump to the Rewrite Description tool"
                />
                <ActionButton
                    icon={isRefreshing ? RefreshCw : DatabaseZap}
                    text={isRefreshing ? "Refreshing..." : "Refresh Tags"}
                    onClick={onRefreshTags}
                    disabled={isRefreshing}
                    title="Re-scrape this product's tags (Grades, Subjects, etc.) from TPT"
                />
                <ActionButton
                    icon={CheckSquare}
                    text="Mark Updated"
                    onClick={() => onMarkProductUpdated?.(product.id)}
                    disabled={isRefreshing}
                    title="Mark description/title as recently updated"
                />
                <ActionButton
                    icon={CheckSquare}
                    text="Uploaded to TPT"
                    onClick={() => onMarkUploadedToTpt?.(product.id)}
                    disabled={isRefreshing}
                    title="Confirm you’ve uploaded changes to TPT"
                />
              </div>

              <div className="text-xs text-slate-500 space-x-2">
                {(product.lastUpdated || product.tptUploadedAt) ? (
                    <>
                        {product.lastUpdated && (<span>Updated: {formatTimestamp(product.lastUpdated)}</span>)}
                        {product.tptUploadedAt && (<span>TPT Upload: {formatTimestamp(product.ttUploadedAt)}</span>)}
                    </>
                ) : (
                    <span>No recent updates logged</span>
                )}
              </div>
            </div>
        </div>
        <ChevronRight className={`text-slate-300 shrink-0 transition-transform ${selected ? 'rotate-90' : ''}`} />
      </div>
    </div>
  );
};

export default ProductRow;